import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { rolesPermissionsService } from '../../../services/rolesPermissionsService/rolesPermissionsService';
import {
  Role,
  RolesPermissions,
  RolesPermissionsFilterParams,
  RolesPermissionsResponse,
  RolesPermissionsUpdate,
} from '../../../types/rolesPermissions.types';

interface RolesPermissionState {
  list: {
    data: RolesPermissions[];
  };
  selected: RolesPermissions | null;
  loading: boolean;
  error: string | null;
  filteredRoles: Role[];
}

const initialState: RolesPermissionState = {
  list: {
    data: [],
  },
  selected: null,
  loading: false,
  error: null,
  filteredRoles: [],
};

// Get All Roles
export const getAllRolesDropdown = createAsyncThunk(
  'adminUsers/getAllRoles',
  async (_, thunkAPI) => {
    try {
      const response = await rolesPermissionsService.getAllRolesDropdown();
      return response.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getAllRoles = createAsyncThunk<
  RolesPermissionsResponse,
  RolesPermissionsFilterParams | undefined,
  { rejectValue: string }
>('adminUsers/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await rolesPermissionsService.getAllRoles(params);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch admin users');
  }
});

export const getRoleById = createAsyncThunk<RolesPermissions, number>(
  'roles-permissions/getById',
  async (id: number, thunkAPI) => {
    try {
      return (await rolesPermissionsService.getRoleById(id)) as RolesPermissions;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || 'Error getting RolesPermissions',
      );
    }
  },
);

export const deleteRole = createAsyncThunk(
  'roles-permissions/softDelete',
  async ({ id, deletedBy }: { id: string; deletedBy: string }, thunkAPI) => {
    try {
      return await rolesPermissionsService.deleteRole(id, deletedBy);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || 'Error deleting RolesPermissions',
      );
    }
  },
);

export const deleteRoleById = createAsyncThunk(
  'roles-permissions/delete/roleById',
  async (id: string, thunkAPI) => {
    try {
      return await rolesPermissionsService.deleteRoleById(id);
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Error deleting role';
      return thunkAPI.rejectWithValue(apiMessage);
    }
  },
);

export const updateRole = createAsyncThunk(
  'adminUsers/update',
  async ({ id, payload }: { id?: number; payload?: RolesPermissionsUpdate }, thunkAPI) => {
    try {
      const response = await rolesPermissionsService.updateRole(id, payload);
      return response;
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Error updating role';
      return thunkAPI.rejectWithValue(apiMessage);
    }
  },
);

const rolesPermissionsSlice = createSlice({
  name: 'rolesPermissions',
  initialState,
  reducers: {
    clearRolesError(state) {
      state.error = null;
    },
    updateRoleInList(
      state,
      action: PayloadAction<{ id: number; payload: Partial<RolesPermissions> }>,
    ) {
      const index = state.list.data.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.list.data[index] = {
          ...state.list.data[index],
          ...action.payload.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllRoles.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        state.loading = false;
        const payload: any = action.payload;
        if (Array.isArray(payload)) {
          state.list.data = payload;
        } else if (Array.isArray(payload?.data)) {
          state.list.data = payload.data;
        } else if (Array.isArray(payload?.items)) {
          state.list.data = payload.items;
        } else {
          state.list.data = [];
        }
      })
      .addCase(getAllRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder.addCase(getAllRolesDropdown.fulfilled, (state, action) => {
      state.loading = false;
      state.filteredRoles = action.payload;
    });

    builder.addCase(getRoleById.fulfilled, (state) => {
      state.loading = false;
    });

    builder.addCase(deleteRole.fulfilled, (state) => {
      state.loading = false;
    });
  },
});

export const { clearRolesError, updateRoleInList } = rolesPermissionsSlice.actions;
export default rolesPermissionsSlice.reducer;
